import { useMemo, useState } from "react";
import { Eye } from "lucide-react";
import moment from "moment";

import { SafeHtml } from "components/SafeHtml/SafeHtml";
import { DataTable } from "components/ui/DataTable";
import { Dialog } from "components/ui/Dialog";
import { Pill, PILL_COLORS } from "components/ui/Pill";

import s from "./SentEmailsTable.module.css";

const getStatusPill = (status) => {
  if (!status) return PILL_COLORS.statusNoStatus;
  const map = {
    SENT: PILL_COLORS.statusSent,
    ERROR: PILL_COLORS.statusError,
    PENDING: PILL_COLORS.statusPending,
    SUCCESS: PILL_COLORS.statusSuccess,
    ACKNOWLEDGED: PILL_COLORS.statusAcknowledged,
    WARNING: PILL_COLORS.statusWarning,
  };
  return map[status.toUpperCase()] ?? PILL_COLORS.statusNoStatus;
};

const formatStatus = (status) => {
  if (!status) return "-";
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
};

const columns = [
  {
    id: "subject",
    header: "Subject",
    render: (email) => email.subject || "No Subject",
  },
  {
    id: "sentTo",
    header: "Sent To",
    render: (email) => email.emailAddress || "-",
  },
  {
    id: "status",
    header: "Status",
    render: (email) => (
      <Pill {...getStatusPill(email.status)}>{formatStatus(email.status)}</Pill>
    ),
  },
  {
    id: "sentDate",
    header: "Sent Date",
    render: (email) =>
      email.sentAt ? moment(email.sentAt).format("DD-MM-YYYY HH:mm") : "-",
  },
];

export const SentEmailsTable = ({ emails }) => {
  const [previewEmail, setPreviewEmail] = useState(null);

  const rowActions = useMemo(
    () => [
      {
        label: "Preview",
        icon: Eye,
        primary: true,
        onClick: (email) => setPreviewEmail(email),
        hidden: (email) => !email.body,
      },
    ],
    []
  );

  return (
    <div className={s.section}>
      <DataTable
        columns={columns}
        data={emails}
        getRowId={(row) => row.id}
        emptyTitle="No emails found"
        emptyText="No emails sent for this consultation."
        rowsPerPage={20}
        rowsOptions={[5, 10, 20, 50, "All"]}
        rowActions={rowActions}
      />
      <Dialog
        title={previewEmail?.subject || "Email Preview"}
        open={!!previewEmail}
        onClose={() => setPreviewEmail(null)}
        width={1024}
      >
        {!!previewEmail && (
          <div>
            <div className={s.previewMeta}>
              <span className={s.previewMetaLabel}>To:</span>
              <span>{previewEmail.emailAddress}</span>
              <span className={s.previewMetaLabel}>Sent:</span>
              <span>
                {previewEmail.sentAt
                  ? moment(previewEmail.sentAt).format("DD-MM-YYYY HH:mm")
                  : "-"}
              </span>
            </div>
            <SafeHtml html={previewEmail.body} className={s.previewBody} />
          </div>
        )}
      </Dialog>
    </div>
  );
};
